/**
 * The NAVIGATION TRAIL — how a record remembers where you came from.
 *
 * Christopher, 2026-08-06, after walking organization → People (child grid) →
 * a contact: "when you go back, you don't go back to organizations, but to
 * general contacts. one would expect to be back on the organization grid you
 * were before." And: "there are no breadcrumbs … especially with the cascading
 * grids we have, that we also depict the path we followed."
 *
 * Both are one defect: navigation THROUGH a relationship threw the relationship
 * away. The child-grid link was a plain `<Link to="/e/$entity/$id">`, so the
 * record page could only guess its origin — and it guessed "the entity's own
 * list", which is wrong exactly when the journey mattered.
 *
 * ── Model ──────────────────────────────────────────────────────────────────
 * A trail is the list of crumbs that LED to a record, written by whoever
 * navigates there, read once by the record page:
 *
 *   { for: '/e/core_person/p1',
 *     crumbs: [ { label: 'Organizations', to: '/e/core_organization' },
 *               { label: 'Moonbase',      to: '/e/core_organization/g1' } ] }
 *
 * `for` is the guard: the page compares it to its own location and IGNORES a
 * trail written for somewhere else — a stale trail must degrade to the default
 * back-to-list, never to a wrong path presented confidently.
 *
 * sessionStorage, not a module variable, so a refresh mid-journey keeps the
 * path (the tab is the journey's scope); not localStorage, because yesterday's
 * path to a record is not today's.
 */
const KEY = 'solum.trail'

export interface Crumb { label: string; to: string }
interface Stored { for: string; crumbs: Crumb[] }

function read(): Stored | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as Stored) : null
  } catch { return null }
}

/** Write the trail for a navigation that is ABOUT to happen. */
export function setTrail(target: string, crumbs: Crumb[]) {
  try { sessionStorage.setItem(KEY, JSON.stringify({ for: target, crumbs: crumbs.slice(-8) })) } catch { /* private mode */ }
}

/**
 * The trail that led HERE, or null. Reading does not clear — a re-render or a
 * refresh must see the same answer. The trail is replaced by the next
 * `setTrail`, or invalidated by navigating somewhere it was not written for.
 */
export function trailFor(pathname: string): Crumb[] | null {
  const t = read()
  if (!t || t.for !== pathname) return null
  return t.crumbs.length ? t.crumbs : null
}

/**
 * The ancestors a record page may draw around its one, real title.
 *
 * A trail is written by the surface that navigates to a record, so malformed
 * or old callers can accidentally include the destination itself. Rendering
 * that raw value made the same record name appear in the back control, the
 * breadcrumb and the page heading. The heading is the identity; chrome only
 * names places the user can go back to.
 *
 * Filtering by destination rather than label is deliberate: two different
 * records may honestly have the same display name.
 */
export function recordAncestors(pathname: string, fallback: Crumb): Crumb[] {
  const candidates = trailFor(pathname) ?? [fallback]
  const ancestors = candidates.filter((crumb, index) => (
    crumb.to !== pathname && (index === 0 || candidates[index - 1]?.to !== crumb.to)
  ))
  return ancestors.length ? ancestors : [fallback]
}

/**
 * Continue a journey: the crumbs that led to the CURRENT page, plus the current
 * page itself — written for the next target. `self` names where the user is
 * standing (its label is the record's heading).
 */
export function extendTrail(currentPath: string, self: Crumb, target: string) {
  const here = trailFor(currentPath) ?? []
  // A cycle (record → … → same record) would grow forever; cut back to the
  // first visit instead, which is also what a reader expects a breadcrumb to do.
  const at = here.findIndex((c) => c.to === self.to)
  setTrail(target, at >= 0 ? [...here.slice(0, at), self] : [...here, self])
}
